import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button, Checkbox, Flex, Tooltip } from 'antd';
import { LuCheck, LuPencil, LuPentagon, LuX } from 'react-icons/lu';
import VectorSource from 'ol/source/Vector';

import ConfirmDetectionModal from '@components/detection/ConfirmDetectionModal';
import { useProject } from '@contexts/Project.context';
import { useBoundaryEditor } from '@hooks/useBoundaryEditor';
import { useMapToolBusy } from '@hooks/useMapToolBusy';
import { useWebGLVectorLayer } from '@hooks/useWebGLVectorLayer';
import { useAutoFitBoundary } from '@hooks/useAutoFitBoundary';
import { getCollectionDetectedArea } from '@utils/api';
import { formatAoiArea, getAoiStats } from '@utils/aoiStats';
import { getScanAreaSquareMeters } from '@utils/detectionOutcome';
import { createFeature, noOverlapOffset } from '@utils/map/helpers';
import { COLLECTION_LAYER_ID } from '@utils/constants';

const LAYER_ID = COLLECTION_LAYER_ID.BOUNDARY;
const STROKE_WIDTH = 4;
const STYLE = {
	'stroke-color': 'rgba(255, 255, 0, 0.8)',
	'stroke-width': STROKE_WIDTH,
	'stroke-offset': noOverlapOffset(STROKE_WIDTH),
	'stroke-line-dash': [10, 10],
};

/**
 * Renders the collection's detected-area boundary (the region actually
 * scanned) on the map, with a toggle to show/hide it. Mirrors AoiLayer's
 * "Model boundary" but for collection-based detection, sourced from
 * GET /collections/{collectionUuid}/detected-area.
 *
 * The boundary is editable: dragging its vertices and confirming asks the
 * backend what re-running detection on the new shape would do, and a
 * confirmation dialog puts that decision in the user's hands before any job
 * starts. Declining restores the original boundary.
 *
 * @param {object} props
 * @param {string} props.collectionUuid - the collection UUID
 * @param {string} [props.collectionName] - shown in the confirmation dialog
 * @param {Array<{key: string, label: string, color?: string}>} [props.detectionObjects] -
 *   the objects the collection detects, listed in the confirmation dialog
 */
const CollectionBoundaryLayer = ({ collectionUuid, collectionName, detectionObjects }) => {
	const { mapObject, project, dispatch } = useProject();
	const { busy: mapBusy, reason: blockedReason } = useMapToolBusy();
	const { uuid: projectUuid } = project ?? {};

	const { data: rawFeatures } = useQuery({
		queryKey: ['collectionDetectedArea', projectUuid, collectionUuid],
		queryFn: () => getCollectionDetectedArea(projectUuid, collectionUuid),
		enabled: !!projectUuid && !!collectionUuid,
		refetchInterval: 60_000 * 5, // 5min
		refetchIntervalInBackground: true,
	});

	const { fitToExtent } = useAutoFitBoundary(mapObject, `${projectUuid}-${collectionUuid}`);

	// Sits below the collection detection shapes (102-104) so the dashed
	// outline doesn't visually compete with them.
	const layer = useWebGLVectorLayer(mapObject, LAYER_ID, {
		name: 'Boundary',
		zIndex: 101,
		visible: true,
	});

	const features = useMemo(
		() =>
			(rawFeatures ?? [])
				.map(({ geometry, properties }) => createFeature(geometry, properties))
				.filter(Boolean),
		[rawFeatures]
	);

	const {
		isEditing,
		isSaving,
		startEdit,
		cancelEdit,
		confirmEdit,
		pendingConfirmation,
		editedRing,
		confirmRun,
		declineRun,
	} = useBoundaryEditor({
		layer,
		mapObject,
		dispatch,
		collectionUuid,
		projectUuid,
		// Starting an edit while another tool owns the map would put two
		// interactions on it, sharing pointer events per-pixel.
		mapBusy,
	});

	// Area of the shape the user drew, so the confirmation says how big the new
	// boundary is alongside the server's estimate of the ground a run would scan.
	const editedAreaLabel = useMemo(
		() => formatAoiArea(getAoiStats(editedRing)?.squareMeters),
		[editedRing]
	);

	// Skipped while editing — otherwise a background refetch would swap in a
	// fresh VectorSource, orphaning the live Modify interaction and discarding
	// the in-progress edit.
	useEffect(() => {
		if (!layer || isEditing) return;
		layer.setStyle(STYLE);
		layer.setSource(new VectorSource({ features }));

		// Once per collection load, if the boundary extends beyond the current
		// view, fit the map to it so the full scanned area — and every
		// detection within it — is visible without manual zooming out.
		fitToExtent(layer);
	}, [layer, features, isEditing, fitToExtent]);

	// Mirrors the layer's own visibility rather than owning it, so external
	// changes (e.g. another control toggling this layer) stay in sync.
	const [visible, setVisible] = useState(true);
	useEffect(() => {
		if (!layer) return;
		setVisible(layer.getVisible());
		const handleChange = () => setVisible(layer.getVisible());
		layer.on('change:visible', handleChange);
		return () => layer.un('change:visible', handleChange);
	}, [layer]);

	// Editing a hidden boundary would drag vertices nobody can see.
	const editTooltip =
		blockedReason || (visible ? 'Edit boundary' : 'Show the boundary to edit it');

	if (!layer || (!features.length && !isEditing)) return null;

	return (
		<li>
			<ConfirmDetectionModal
				open={Boolean(pendingConfirmation)}
				onConfirm={confirmRun}
				onCancel={declineRun}
				confirming={isSaving}
				title="Re-run detection?"
				confirmText="Re-run detection"
				cancelText="Discard changes"
				heading="Your edited boundary needs a new run"
				description="Detection hasn't started. Discarding restores the original boundary."
				collectionName={collectionName}
				objects={detectionObjects}
				areaLabel={`Edited boundary${editedAreaLabel ? ` · ${editedAreaLabel}` : ''}`}
				areaIcon={<LuPentagon />}
				scanAreaSquareMeters={getScanAreaSquareMeters(pendingConfirmation)}
			/>

			<Flex justify="space-between" align="center">
				<Checkbox
					checked={visible}
					disabled={isEditing}
					onChange={e => layer.setVisible(e.target.checked)}>
					Boundary
				</Checkbox>
				{isEditing ? (
					<Flex gap={2}>
						<Tooltip title="Cancel">
							<Button
								type="text"
								size="small"
								icon={<LuX />}
								onClick={cancelEdit}
								disabled={isSaving}
								aria-label="Cancel boundary edit"
							/>
						</Tooltip>
						<Tooltip title="Confirm">
							<Button
								type="text"
								size="small"
								icon={<LuCheck />}
								onClick={confirmEdit}
								loading={isSaving}
								aria-label="Confirm boundary edit"
							/>
						</Tooltip>
					</Flex>
				) : (
					<Tooltip title={editTooltip}>
						{/* antd removes pointer events from disabled buttons, so the wrapper
						    keeps the tooltip hoverable while the button is disabled. */}
						<span>
							<Button
								type="text"
								size="small"
								icon={<LuPencil />}
								onClick={startEdit}
								disabled={mapBusy || !visible}
								aria-label="Edit Boundary"
							/>
						</span>
					</Tooltip>
				)}
			</Flex>
		</li>
	);
};

export default CollectionBoundaryLayer;
